import { useState } from 'react'
import { User, Building2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Client, ClientType, CreateClientDto } from '../types/client'
import { clientService } from '../services/client-service'

interface QuickClientModalProps {
  isOpen: boolean
  onClose: () => void
  onClientCreated: (client: Client) => void
}

export function QuickClientModal({ isOpen, onClose, onClientCreated }: QuickClientModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [clientType, setClientType] = useState<ClientType>('PERSON')

  // Person fields
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [cpf, setCpf] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  // Company fields
  const [companyName, setCompanyName] = useState('')
  const [tradingName, setTradingName] = useState('')
  const [cnpj, setCnpj] = useState('')
  const [companyEmail, setCompanyEmail] = useState('')
  const [companyPhone, setCompanyPhone] = useState('')

  const resetForm = () => {
    setFirstName('')
    setLastName('')
    setCpf('')
    setEmail('')
    setPhone('')
    setCompanyName('')
    setTradingName('')
    setCnpj('')
    setCompanyEmail('')
    setCompanyPhone('')
    setError(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (clientType === 'PERSON' && (!firstName.trim() || !lastName.trim())) {
      setError('Nome e sobrenome são obrigatórios')
      return
    }

    if (clientType === 'COMPANY' && !companyName.trim()) {
      setError('Razão social é obrigatória')
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Build contacts array
      type ContactInput = { contactType: 'EMAIL' | 'WHATSAPP'; value: string; isPrimary: boolean; isVerified: boolean }
      const contacts: ContactInput[] = []

      if (clientType === 'PERSON') {
        if (email.trim()) {
          contacts.push({
            contactType: 'EMAIL',
            value: email.trim(),
            isPrimary: true,
            isVerified: false
          })
        }
        if (phone.trim()) {
          contacts.push({
            contactType: 'WHATSAPP',  // Backend espera WHATSAPP, não PHONE
            value: phone.trim().replace(/\D/g, ''),  // Remove formatação
            isPrimary: true,
            isVerified: false
          })
        }
      } else {
        if (companyEmail.trim()) {
          contacts.push({
            contactType: 'EMAIL',
            value: companyEmail.trim(),
            isPrimary: true,
            isVerified: false
          })
        }
        if (companyPhone.trim()) {
          contacts.push({
            contactType: 'WHATSAPP',  // Backend espera WHATSAPP, não PHONE
            value: companyPhone.trim().replace(/\D/g, ''),  // Remove formatação
            isPrimary: true,
            isVerified: false
          })
        }
      }

      // Prepare personalData
      const personalData: { fullName: string; firstName: string; lastName: string; cpf?: string } = {
        fullName: `${firstName.trim()} ${lastName.trim()}`,
        firstName: firstName.trim(),
        lastName: lastName.trim()
      }

      // Only add CPF if it has at least 11 digits
      if (cpf.trim()) {
        const cleanCpf = cpf.replace(/\D/g, '')
        if (cleanCpf.length === 11) {
          personalData.cpf = cleanCpf
        }
      }

      // Prepare companyData
      const companyData: { companyName: string; tradeName: string; tradingName: string; cnpj?: string } = {
        companyName: companyName.trim(),
        tradeName: tradingName.trim() || companyName.trim(),
        tradingName: tradingName.trim() || companyName.trim()
      }

      // Only add CNPJ if it has at least 14 digits
      if (cnpj.trim()) {
        const cleanCnpj = cnpj.replace(/\D/g, '')
        if (cleanCnpj.length === 14) {
          companyData.cnpj = cleanCnpj
        }
      }

      const clientData: CreateClientDto = {
        clientType,
        status: 'ACTIVE',
        contacts,
        ...(clientType === 'PERSON' && { personalData }),
        ...(clientType === 'COMPANY' && { companyData })
      }

      console.log('📤 Sending client data:', JSON.stringify(clientData, null, 2))

      const newClient = await clientService.createClient(clientData)
      resetForm()
      onClientCreated(newClient)
      onClose()
    } catch (err: unknown) {
      console.error('❌ Error creating client:', err)
      const errObj = err as { response?: { data?: { error?: { details?: Array<{ message: string }>; message?: string }; message?: string } } }
      const responseData = errObj.response?.data
      console.error('❌ Error response:', JSON.stringify(responseData, null, 2))

      let errorMessage = 'Erro ao criar cliente'
      if (responseData?.error?.details) {
        errorMessage = responseData.error.details.map((d) => d.message).join(', ')
      } else if (responseData?.error?.message) {
        errorMessage = responseData.error.message
      } else if (responseData?.message) {
        errorMessage = responseData.message
      }

      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent size="md" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cadastro Rápido de Cliente</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Client Type Toggle */}
          <div className="space-y-2">
            <Label id="quick-client-type-label">Tipo de Cliente *</Label>
            <div className="grid grid-cols-2 gap-3" role="group" aria-labelledby="quick-client-type-label">
              <Button
                type="button"
                variant={clientType === 'PERSON' ? 'default' : 'outline'}
                aria-pressed={clientType === 'PERSON'}
                onClick={() => setClientType('PERSON')}
              >
                <User className="size-4" />
                Pessoa Física
              </Button>
              <Button
                type="button"
                variant={clientType === 'COMPANY' ? 'default' : 'outline'}
                aria-pressed={clientType === 'COMPANY'}
                onClick={() => setClientType('COMPANY')}
              >
                <Building2 className="size-4" />
                Pessoa Jurídica
              </Button>
            </div>
          </div>

          {/* Person Fields */}
          {clientType === 'PERSON' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName">Nome *</Label>
                  <Input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName">Sobrenome *</Label>
                  <Input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cpf">CPF</Label>
                <Input
                  type="text"
                  id="cpf"
                  value={cpf}
                  onChange={(e) => setCpf(e.target.value)}
                  placeholder="000.000.000-00"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>
          )}

          {/* Company Fields */}
          {clientType === 'COMPANY' && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="companyName">Razão Social *</Label>
                <Input
                  type="text"
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tradingName">Nome Fantasia</Label>
                <Input
                  type="text"
                  id="tradingName"
                  value={tradingName}
                  onChange={(e) => setTradingName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cnpj">CNPJ</Label>
                <Input
                  type="text"
                  id="cnpj"
                  value={cnpj}
                  onChange={(e) => setCnpj(e.target.value)}
                  placeholder="00.000.000/0000-00"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="companyEmail">E-mail</Label>
                <Input
                  type="email"
                  id="companyEmail"
                  value={companyEmail}
                  onChange={(e) => setCompanyEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="companyPhone">Telefone</Label>
                <Input
                  type="tel"
                  id="companyPhone"
                  value={companyPhone}
                  onChange={(e) => setCompanyPhone(e.target.value)}
                  placeholder="(00) 0000-0000"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" loading={loading}>
              {loading ? 'Criando...' : 'Criar Cliente'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
